import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, FileJson, Link2, Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import WorkflowImportPreview from './WorkflowImportPreview';
import {
  importAgencyWorkflowFile,
  importAgencyWorkflowUrl,
  importPlatformWorkflowFile,
  importPlatformWorkflowUrl,
  previewAgencyWorkflowImport,
  previewAgencyWorkflowUrl,
  previewPlatformWorkflowImport,
  previewPlatformWorkflowImportUrl,
  readWorkflowImportFile,
  safeImportErrorMessage,
  validateHttpsImportUrl,
} from '../../services/workflowPortableService';

const IDLE = 'idle';
const READING = 'reading';
const FETCHING = 'fetching';
const VALIDATING = 'validating';
const PREVIEW = 'preview';
const IMPORTING = 'importing';
const ERROR = 'error';

const AGENCY_ID_REQUIRED_ERROR = 'Agency id is required to import a workflow.';

/**
 * Unified dialog for Import from file and Import from URL.
 * mode: 'file' | 'url'
 * scope: 'platform' | 'agency'
 *
 * Supports both:
 * - open / onOpenChange (preferred)
 * - isOpen / onClose (legacy callers)
 */
const WorkflowImportDialog = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  mode = 'file',
  scope = 'platform',
  agencyId,
  token,
  onImported,
}) => {
  const dialogOpen = typeof open === 'boolean' ? open : Boolean(isOpen);
  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);
  const [state, setState] = useState(IDLE);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [packageJson, setPackageJson] = useState(null);
  const [url, setUrl] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');

  const reset = () => {
    setState(IDLE);
    setError('');
    setPreview(null);
    setPackageJson(null);
    setUrl('');
    setSelectedFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!dialogOpen) reset();
  }, [dialogOpen, mode, scope]);

  const closeDialog = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      if (state === IMPORTING) return;
      reset();
      closeDialog();
      return;
    }
    onOpenChange?.(true);
  };

  const busy = [READING, FETCHING, VALIDATING, IMPORTING].includes(state);

  const assertAgencyScope = () => {
    if (scope !== 'agency') return true;
    if (agencyId) return true;
    setState(ERROR);
    setError(AGENCY_ID_REQUIRED_ERROR);
    return false;
  };

  const previewFile = async (parsed) => {
    if (scope === 'agency') {
      return previewAgencyWorkflowImport(token, agencyId, parsed);
    }
    return previewPlatformWorkflowImport(token, parsed);
  };

  const previewUrl = async (httpsUrl) => {
    if (scope === 'agency') {
      return previewAgencyWorkflowUrl(token, agencyId, httpsUrl);
    }
    return previewPlatformWorkflowImportUrl(token, httpsUrl);
  };

  const importFile = async (parsed) => {
    if (scope === 'agency') {
      return importAgencyWorkflowFile(token, agencyId, parsed);
    }
    return importPlatformWorkflowFile(token, parsed);
  };

  const importUrl = async (httpsUrl) => {
    if (scope === 'agency') {
      return importAgencyWorkflowUrl(token, agencyId, httpsUrl);
    }
    return importPlatformWorkflowUrl(token, httpsUrl);
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!assertAgencyScope()) return;
    setSelectedFileName(file.name);
    setError('');
    setPreview(null);
    setPackageJson(null);
    setState(READING);
    try {
      const parsed = await readWorkflowImportFile(file);
      setPackageJson(parsed);
      setState(VALIDATING);
      const result = await previewFile(parsed);
      setPreview(result.preview || result);
      setState(PREVIEW);
    } catch (err) {
      setState(ERROR);
      setError(safeImportErrorMessage(err, 'Invalid JSON file'));
    }
  };

  const currentUrlValue = () =>
    (urlInputRef.current?.value ?? url ?? '').trim();

  const handleFetchUrl = async () => {
    if (!assertAgencyScope()) return;
    const nextUrl = currentUrlValue();
    setUrl(nextUrl);
    const check = validateHttpsImportUrl(nextUrl);
    if (!check.ok) {
      setState(ERROR);
      setError(check.error);
      return;
    }
    setError('');
    setPreview(null);
    setPackageJson(null);
    setState(FETCHING);
    try {
      setState(VALIDATING);
      const result = await previewUrl(check.url);
      setPreview(result.preview || result);
      if (result.package) setPackageJson(result.package);
      setState(PREVIEW);
    } catch (err) {
      setState(ERROR);
      setError(safeImportErrorMessage(err, 'Unable to fetch workflow from URL'));
    }
  };

  const resolveImportedWorkflow = (result) => {
    if (result?.workflow && typeof result.workflow === 'object') {
      return result.workflow;
    }
    if (result?.id || result?.workflow_id) {
      return result;
    }
    return null;
  };

  const handleImport = async () => {
    if (busy) return;
    if (!assertAgencyScope()) return;
    setState(IMPORTING);
    setError('');
    try {
      let result;
      if (mode === 'url') {
        const nextUrl = currentUrlValue();
        setUrl(nextUrl);
        const check = validateHttpsImportUrl(nextUrl);
        if (!check.ok) {
          setState(ERROR);
          setError(check.error);
          return;
        }
        result = await importUrl(check.url);
      } else {
        if (!packageJson) {
          setState(ERROR);
          setError('Invalid JSON file');
          return;
        }
        result = await importFile(packageJson);
      }
      const workflow = resolveImportedWorkflow(result);
      const workflowId = workflow?.id || workflow?.workflow_id;
      if (!workflowId) {
        setState(ERROR);
        setError('Import succeeded but workflow id was missing');
        return;
      }
      onImported?.(workflow);
    } catch (err) {
      setState(ERROR);
      setError(safeImportErrorMessage(err, 'Unable to import workflow'));
    }
  };

  const title = mode === 'url' ? 'Import from URL' : 'Import from file';
  const description =
    mode === 'url'
      ? 'Paste a public HTTPS URL to a FundElite workflow JSON file.'
      : 'Select a .json or .fundelite.json portable workflow file.';

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg bg-zinc-900 border-zinc-700 text-white"
        data-testid="workflow-import-dialog"
        data-mode={mode}
        data-scope={scope}
        data-state={state}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'url' ? <Link2 className="h-5 w-5 text-amber-500" /> : <Upload className="h-5 w-5 text-amber-500" />}
            {title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {mode === 'file' ? (
            <div className="space-y-2">
              <Label className="text-zinc-300">Workflow file</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.fundelite.json,application/json"
                className="hidden"
                data-testid="workflow-import-file-input"
                onChange={handleFileSelected}
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start border-zinc-600 text-zinc-200"
                onClick={() => {
                  if (!assertAgencyScope()) return;
                  fileInputRef.current?.click();
                }}
                disabled={busy}
                data-testid="workflow-import-file-picker"
              >
                <FileJson className="mr-2 h-4 w-4" />
                {selectedFileName || 'Choose .json / .fundelite.json file'}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="workflow-import-url" className="text-zinc-300">
                Public HTTPS URL
              </Label>
              <div className="flex gap-2">
                <Input
                  id="workflow-import-url"
                  ref={urlInputRef}
                  defaultValue=""
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/workflow.fundelite.json"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="workflow-import-url-input"
                  disabled={busy}
                />
                <Button
                  type="button"
                  onClick={handleFetchUrl}
                  disabled={busy}
                  className="bg-amber-500 text-black hover:bg-amber-600"
                  data-testid="workflow-import-url-fetch"
                >
                  {state === FETCHING || state === VALIDATING ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Fetch'
                  )}
                </Button>
              </div>
            </div>
          )}

          {error ? (
            <div
              className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300"
              data-testid="workflow-import-error"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {preview ? <WorkflowImportPreview preview={preview} /> : null}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-zinc-600 text-zinc-300"
            onClick={() => handleOpenChange(false)}
            disabled={state === IMPORTING}
            data-testid="workflow-import-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={busy || state !== PREVIEW || !preview}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="workflow-import-as-draft"
          >
            {state === IMPORTING ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              'Import as Draft'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowImportDialog;
